import { getCentroidCoordinates } from "@phc/common"
import type {
  BiomeshSensorResponse,
  OperationalStatus,
} from "@phc/common/gen/threatAnalyticsGen"
import {
  useListPlatformsBiomeshPlatformListGet,
  useListSensorsBiomeshSensorListGet,
} from "@phc/common/gen/threatAnalyticsGen"
import type { BBox, Feature, Point } from "geojson"
import { useCallback, useMemo } from "react"
import Supercluster from "supercluster"
import { groupSensorsByNode, useBioMeshUIState } from "./BioMeshUIStateContext"

export const MAX_CLUSTER_ZOOM = 16

/** Properties stored on each GeoJSON point fed into supercluster. */
export interface NodePointProps {
  platform_id: string
  name: string
  platform_type: string
  /** Pre-computed sensor status counts for this single node. */
  fmc: number
  pmc: number
  nmc: number
  total: number
}

/** Aggregated status counts carried on a cluster node. */
export interface ClusterProps {
  cluster: true
  cluster_id: number
  point_count: number
  fmc: number
  pmc: number
  nmc: number
  total: number
}

export type NodeFeature = Feature<Point, NodePointProps>

export type ClusterOrPoint =
  | Supercluster.ClusterFeature<ClusterProps>
  | Supercluster.PointFeature<NodePointProps>

export function isCluster(
  c: ClusterOrPoint
): c is Supercluster.ClusterFeature<ClusterProps> {
  return "cluster" in c.properties && c.properties.cluster
}

function countStatus(
  sensors: BiomeshSensorResponse[],
  status: OperationalStatus
) {
  return sensors.filter(s => s.operational_status === status).length
}

/**
 * Builds a supercluster index from node + sensor data and
 * returns a function to query visible clusters for a given viewport.
 */
export function useNodeClusters() {
  const { data: nodes } = useListPlatformsBiomeshPlatformListGet()
  const { data: sensors } = useListSensorsBiomeshSensorListGet({ limit: 10000 })
  const { filters } = useBioMeshUIState()

  /** Map of platform_id → filtered sensors[] */
  const sensorsByNode = useMemo(
    () => groupSensorsByNode(sensors, filters),
    [sensors, filters]
  )

  /** GeoJSON features for all nodes (sensor counts reflect active filters). */
  const points = useMemo<NodeFeature[]>(() => {
    if (!nodes?.length) return []
    return nodes.flatMap(p => {
      const coords = getCentroidCoordinates(p.centroid)
      if (!coords) return []
      const pSensors = sensorsByNode.get(p.platform_id) ?? []
      return [
        {
          type: "Feature" as const,
          properties: {
            platform_id: p.platform_id,
            name: p.name,
            platform_type: p.platform_type,
            fmc: countStatus(pSensors, "OPERATIONAL_STATUS_FMC"),
            pmc: countStatus(pSensors, "OPERATIONAL_STATUS_PMC"),
            nmc: countStatus(pSensors, "OPERATIONAL_STATUS_NMC"),
            total: pSensors.length,
          },
          geometry: {
            type: "Point" as const,
            coordinates: [coords.lng, coords.lat],
          },
        },
      ]
    })
  }, [nodes, sensorsByNode])

  /** Build the supercluster index whenever points change. */
  const index = useMemo(() => {
    const sc = new Supercluster<
      NodePointProps,
      Pick<ClusterProps, "fmc" | "pmc" | "nmc" | "total">
    >({
      radius: 60,
      maxZoom: MAX_CLUSTER_ZOOM,
      reduce(accumulated, props) {
        accumulated.fmc += props.fmc
        accumulated.pmc += props.pmc
        accumulated.nmc += props.nmc
        accumulated.total += props.total
      },
      map(props) {
        return {
          fmc: props.fmc,
          pmc: props.pmc,
          nmc: props.nmc,
          total: props.total,
        }
      },
    })
    sc.load(points)
    return sc
  }, [points])

  /** Get clusters for the current map viewport. */
  const getClusters = useCallback(
    (bounds: BBox, zoom: number): ClusterOrPoint[] =>
      index.getClusters(bounds, Math.floor(zoom)),
    [index]
  )

  /** Get expansion zoom for a cluster (returns MAX_CLUSTER_ZOOM for stale IDs). */
  const getExpansionZoom = useCallback(
    (clusterId: number) => {
      try {
        return index.getClusterExpansionZoom(clusterId)
      } catch {
        return MAX_CLUSTER_ZOOM
      }
    },
    [index]
  )

  /** Get the leaf points inside a cluster (returns [] for stale IDs). */
  const getLeaves = useCallback(
    (clusterId: number, limit = 100): NodeFeature[] => {
      try {
        return index
          .getLeaves(clusterId, limit)
          .filter((c): c is NodeFeature => !("cluster" in c.properties))
      } catch {
        return []
      }
    },
    [index]
  )

  return { getClusters, getExpansionZoom, getLeaves, points, loading: !nodes }
}
